import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import {
  Box,
  Stack,
  Text,
  TextLinkButton,
  useDialogController,
  useToast
} from '@machobear/design-system'
import { Formik, FormikHelpers } from 'formik'
import toInteger from 'lodash/toInteger'
import { useDispatch } from 'react-redux'
import { Userpilot } from 'userpilot'
import * as Yup from 'yup'

import ENV_VARS from '@/config'
import { registerLandlord } from '@/models/auth/ports'
import { createBuilding, createUnit } from '@/ports'
import { breakpoints } from '@/styles'
import { defaultIfFalsy } from '@/utils'
import useCurrentSession from '@/hooks/use-current-session'
import useMediaQuery from '@/hooks/use-media-query'
import useSessionStorage from '@/hooks/use-sessionstorage'
import { useDSLogin } from '@/features/Auth/Context/ds-login-provider'
import DSLoginModal from '@/features/Auth/login-modal/ds-login-modal'
import { AccountType } from '@/features/Auth/login-modal/types'
import { formatFullName } from '@/features/Auth/utils'
import { showSecurityModal } from '@/features/Header/default-header/security-modal'
import ActionSection from '@/features/post-a-listing/components/action-section'
import { showWarningModal } from '@/features/post-a-listing/components/modals/warning-modal'
import PropertyLocation from '@/features/post-a-listing/components/steps/propertyLocation'
import PropertyLocationSearch from '@/features/post-a-listing/components/steps/propertyLocationSearch'
import PropertySpace from '@/features/post-a-listing/components/steps/propertySpace'
import PropertyType from '@/features/post-a-listing/components/steps/propertyType'
import Signup from '@/features/post-a-listing/components/steps/signup'
import {
  CardTitle,
  FormWrapper,
  Left,
  LogoWrapper,
  Right,
  Subtitle,
  Title,
  Wrapper
} from '@/features/post-a-listing/styles/post-a-listing.styles'
import { StyledForm } from '@/features/post-a-listing/styles/steps.styles'
import { Listing } from '@/features/post-a-listing/types/Listing'
import { LivLogoIcon, ShieldIcon } from '@/components/icons'

const { DASHBOARD_URL } = ENV_VARS

type Props = {
  pageType?: 'post-a-listing' | 'boost-a-listing'
}

const PostAListing = ({ pageType = 'post-a-listing' }: Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const logout = () => dispatch.auth.logout()
  const session = useCurrentSession()

  const { visible, showModal, closeModal } = useDialogController()
  const showToast = useToast()
  const { setAfterLogin } = useDSLogin()
  const [currentStep, setCurrentStep] = useState(0)
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.desktop})`)

  const router = useRouter()
  const { query } = router
  const coupon = query.couponCode || ''
  const isAddressPrefilled = query.isAddressPrefilled
  const storageKey = `${coupon}_address`
  const [address] = useSessionStorage(storageKey)

  const initialValues = {
    type: '',
    scope: '',
    suite: '',
    building: '',
    groupListing: '',
    address: '',
    hide_unit_number: false,
    building_id: null,
    building_name: '',
    street_number: '',
    street_name: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    full_name: '',
    email: '',
    password: ''
  }

  const [data, setData] = useState(initialValues)

  const COMPONENTS = {
    0: PropertyType,
    1: PropertySpace,
    2: PropertyLocationSearch,
    3: PropertyLocation,
    4: Signup
  }

  const clickIds = {
    0: 'landlord-postlisting-essentials-property-next',
    1: 'landlord-postlisting-essentials-space-next',
    2: 'landlord-postlisting-essentials-location-next',
    3: 'landlord-postlisting-essentials-createaccount-continue'
  }

  const clickIdsWithCoupon = {
    0: 'abgrowthfree_postalisting_click_propnext',
    1: 'abgrowthfree_postalisting_click_spacenext',
    2: 'abgrowthfree_postalisting_click_locnext',
    3: 'abgrowthfree_postalisting_click_acctcont'
  }

  const clickIdsData: { [key: number]: string } = coupon
    ? clickIdsWithCoupon
    : clickIds
  const clickId = clickIdsData[currentStep] || ''

  const FormComponent = COMPONENTS[currentStep as keyof typeof COMPONENTS]

  const titles = [
    t('l.post_a_listing.steps.type.title', 'What type of property do you own?'),
    t(
      'l.post_a_listing.steps.space.title',
      'How much space are you renting out?'
    ),
    t('l.post_a_listing.steps.location.title', 'Where is it located?'),
    t(
      'l.post_a_listing.steps.register.title',
      "Let's create an account so you can edit and manage your listing."
    )
  ]

  const propertyTypeEventIds: { [key: string]: string } = {
    house: 'abgrowthfree_postalisting_click_house',
    townhouse: 'abgrowthfree_postalisting_click_town',
    condo: 'abgrowthfree_postalisting_click_condo'
  }

  const propertySpaceEventIds: { [key: string]: string } = {
    entire: 'abgrowthfree_postalisting_click_entire',
    partial: 'abgrowthfree_postalisting_click_partial',
    room: 'abgrowthfree_postalisting_click_room'
  }

  const createListing = async (
    values: Listing,
    reset: FormikHelpers<Listing>['resetForm'],
    userId: string
  ) => {
    if (values.building_id === null) {
      const response: any = await createBuilding({
        body: {
          building_type_txt_id:
            values.type === 'condo' ? 'highrise' : values.type,
          country_code: values.country,
          zip: values.zip,
          name: values.building_name,
          street_name: values.street_name,
          street_number: values.street_number,
          city: values.city,
          state: values.state
        }
      })

      try {
        const unit: any = await createUnit(
          {
            body: {
              unit_number: defaultIfFalsy(null)(values.suite),
              building_id: toInteger(response.body.id),
              status: 1,
              gr_unit: 0,
              unit_type_txt_id: values.type,
              unit_type_scope_txt_id: values.scope,
              hide_unit_number: values.hide_unit_number ? 1 : 0,
              listing_info: {
                status: 1,
                is_hidden: 0,
                primary_user_id: toInteger(userId),
                listing_landlords: [toInteger(userId)]
              }
            }
          },
          { id: response.body.id }
        )

        if (unit.response.ok) {
          const firstKey = Object.keys(unit?.body?.listings)[0]
          const listingId = unit?.body?.listings[firstKey].id
          // ====== Added coupond code condition =========
          if (coupon) {
            router.replace(
              {
                pathname: '/register',
                query: {
                  accountType: 'growth',
                  'billing-cycle': 'monthly',
                  couponCode: coupon,
                  listingId
                }
              },
              undefined
            )
            sessionStorage.removeItem(storageKey)
          } else {
            window.location.href = `${DASHBOARD_URL}/listing/create/${listingId}`
          }
        } else {
          return showWarningModal({
            title: t(
              'b.auth.create.existing_unit_modal.title',
              'Oops! This listing already exists.'
            ),
            message: (
              <Trans i18nKey={'b.auth.create.unit_taken_modal.text'}>
                It appears that you have already created a listing for this
                unit. Please refer to the existing listing or create a new
                listing for a different unit.
              </Trans>
            ),
            email: false,
            button: {
              text: t(
                'b.auth.create.existing_listing.button',
                'Go to existing listing'
              ),
              onClick: () => {
                window.location.href = `${DASHBOARD_URL}/listings`
              }
            },
            secondaryButton: {
              text: t(
                'b.auth.create.try_create_new.button',
                'Create new listing'
              ),
              onClick: () => {
                logout()
                reset()
                setCurrentStep(0)
              }
            }
          })
        }
      } catch (err) {
        console.error(err)
      }
    } else {
      try {
        const unit: any = await createUnit(
          {
            body: {
              unit_number: defaultIfFalsy(null)(values.suite),
              building_id: toInteger(values.building_id),
              status: 1,
              gr_unit: toInteger(0),
              unit_type_txt_id: values.type,
              unit_type_scope_txt_id: values.scope,
              hide_unit_number: values.hide_unit_number ? 1 : 0,
              listing_info: {
                status: 1,
                is_hidden: 0,
                primary_user_id: toInteger(userId),
                listing_landlords: [toInteger(userId)]
              }
            }
          },
          { id: values.building_id }
        )

        if (unit.response.ok) {
          const firstKey = Object.keys(unit?.body?.listings)[0]
          const listingId = unit?.body?.listings[firstKey].id
          // ====== Added coupon code condition =========

          if (coupon) {
            router.replace(
              {
                pathname: '/register',
                query: {
                  accountType: 'growth',
                  'billing-cycle': 'monthly',
                  couponCode: coupon,
                  listingId
                }
              },
              undefined
            )
            sessionStorage.removeItem(storageKey)
          } else {
            window.location.href = `${DASHBOARD_URL}/listing/create/${listingId}`
          }
        } else {
          return showWarningModal({
            title: t(
              'b.auth.create.existing_unit_modal.title',
              'Oops! This listing already exists.'
            ),
            message: (
              <Trans i18nKey={'b.auth.create.unit_taken_modal.text'}>
                It appears that you have already created a listing for this
                unit. Please refer to the existing listing or create a new
                listing for a different unit.
              </Trans>
            ),
            email: false,
            button: {
              text: t(
                'b.auth.create.existing_listing.button',
                'Go to existing listing'
              ),
              id: 'landlord-postlisting-essentials-error-existing-listing',
              onClick: () => {
                window.location.href = `${DASHBOARD_URL}/listings`
              }
            },
            secondaryButton: {
              text: t(
                'b.auth.create.try_create_new.button',
                'Create new listing'
              ),
              id: 'landlord-postlisting-essentials-error-create-new-listing',
              onClick: () => {
                logout()
                reset()
                setCurrentStep(0)
              }
            }
          })
        }
      } catch (err) {
        console.error(err)
      }
    }
  }

  const onSubmit = async (values: Listing, actions: FormikHelpers<Listing>) => {
    const isFinalStep = currentStep === 4

    if (!isFinalStep) {
      if (currentStep === 0) {
        window.dataLayer.push({ event: propertyTypeEventIds?.[values?.scope] })
      } else if (currentStep === 1) {
        window.dataLayer.push({ event: propertySpaceEventIds?.[values?.scope] })
      }

      // When address is filled already, skip the location search step
      setCurrentStep(prev =>
        currentStep === 1 && isAddressPrefilled ? 3 : prev + 1
      )
    } else {
      if (session?.id) {
        await logout()
      }

      const [firstName, lastName] = formatFullName(values.full_name)
      try {
        const user: any = await registerLandlord({
          body: {
            first_name: firstName,
            last_name: lastName,
            password: values.password,
            email: values.email,
            allow_renter_call: false,
            agree_to_terms: true
          }
        })

        if (user.response.ok) {
          await createListing(values, actions.resetForm, user?.body?.user_id)

          window.dataLayer.push({
            event: 'landlord-postlisting-essentials-createaccount-submit',
            userEmail: values.email,
            userFirstName: firstName,
            userLastName: lastName,
            userType: 'landlord',
            userId: user?.body?.user_id
          })
          Userpilot.track('goal_landlord_signup_essentials')
        } else if (
          // ======= added check for email already exists and toaster for errors ======
          user.body?.message ===
          'phone_number_or_email_already_exists_in_the_system'
        ) {
          return showWarningModal({
            title: t(
              'b.auth.create.existing_email_modal.title',
              'This email is already registered.'
            ),
            message: (
              <Trans i18nKey={'b.auth.create.email_taken_modal.text'}>
                If you already have a liv.rent account with this email, please
                log in instead. If you are still having trouble, please reach
                out to us at{' '}
                <Link href="mailto:info@liv.rent">support@liv.rent</Link>.
              </Trans>
            ),
            email: true,
            button: {
              text: t('common.login', 'Log In'),
              onClick: () => {
                setAfterLogin(session => {
                  if (session?.id) {
                    createListing(values, actions.resetForm, session.id)
                  }
                })
                showModal()
              }
            },
            secondaryButton: {
              text: t('b.auth.create.try_again.button', 'Try Again')
            }
          })
        } else {
          showToast({ message: user.body?.message, tone: 'critical' })
        }
      } catch (err) {
        console.error(err)
      }
    }
  }
  const zipCodeRegex = {
    us: /^\d{5}(-\d{4})?$/,
    ca: /^[A-Z]\d[A-Z] \d[A-Z]\d$/i
  }

  const typeSchema = Yup.object().shape({
    type: Yup.string().required(t('b.error.required.text', 'Required'))
  })
  const spaceSchema = Yup.object().shape({
    scope: Yup.string().required(t('b.error.required.text', 'Required'))
  })
  const locationSearchSchema = Yup.object().shape({
    street_name: Yup.string().required(t('b.error.required.text', 'Required'))
  })
  const locationSchema = Yup.object().shape({
    building_name: Yup.string().when('type', {
      is: 'condo',
      then: Yup.string().required(t('b.error.required.text', 'Required')),
      otherwise: Yup.string()
    }),
    suite: Yup.string().when('type', {
      is: 'condo',
      then: Yup.string()
        .matches(
          /^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/,
          'Must only contain numbers and letters'
        )
        .required(t('b.error.required.text', 'Required')),
      otherwise: Yup.string().matches(
        /^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/,
        'Must only contain numbers and letters'
      )
    }),
    full_street_name: Yup.string().required(
      t('b.error.required.text', 'Required')
    ),
    city: Yup.string().required(t('b.error.required.text', 'Required')),
    state: Yup.string().required(t('b.error.required.text', 'Required')),
    zip: Yup.string().when('country', {
      is: 'ca',
      then: Yup.string()
        .matches(zipCodeRegex.ca, 'Invalid postal code')
        .required(t('b.error.required.text', 'Required')),
      otherwise: Yup.string()
        .matches(zipCodeRegex.us, 'Invalid zip code')
        .required(t('b.error.required.text', 'Required'))
    }),
    country: Yup.string().required(t('b.error.required.text', 'Required'))
  })
  const signupSchema = Yup.object().shape({
    email: Yup.string()
      .email('Please enter a valid email')
      .required(t('b.error.required.text', 'Required')),
    full_name: Yup.string()
      .matches(
        /^[ A-Za-zÀ-Ÿ-]*$/,
        t(
          'b.profile.name.special_characters.error',
          'Cannot include special characters'
        )
      )
      .matches(
        /^\w+(\s+\w+)+\s*$/,
        t('b.auth.edit.full_name.error', 'Required')
      )
      .required(t('b.error.required.text', 'Required')),
    password: Yup.string()
      .min(8, 'Missing password requirements below')
      .matches(
        /^\S*$/,
        t(
          'b.auth.edit.password_whitespace.error',
          'Whitespace characters not allowed.'
        )
      )
      .matches(
        /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^{}()[\]./\\,>~+<'"`|\-=_;:&*?])[A-Za-z\d!@#$%^{}()[\]./\\,>~+<'"`|\-=_;:&*?]+$/,
        t(
          'b.auth.edit.password_requirements.error',
          'Missing password requirements below'
        )
      )
      .required(t('b.error.required.text', 'Required'))
  })

  const validationSchemaArray = [
    typeSchema,
    spaceSchema,
    locationSearchSchema,
    locationSchema,
    signupSchema
  ]

  useEffect(() => {
    if (isAddressPrefilled) {
      const parsedAddress = address ? JSON.parse(address) : {}
      setData(oldData => ({ ...oldData, ...parsedAddress }))
    }
  }, [isAddressPrefilled, address])

  return (
    <Wrapper>
      <Left>
        <LogoWrapper>
          <Link href="/" style={{ height: '44px', marginRight: '8px' }}>
            <LivLogoIcon width={87} height={44} />
          </Link>
          <div style={{ cursor: 'pointer' }}>
            <ShieldIcon width={25} height={35} onClick={showSecurityModal} />
          </div>
        </LogoWrapper>
        {(currentStep === 0 || !isMobile) && (
          <Box style={{ maxWidth: '700px' }}>
            <Stack space="xxsmall">
              <Stack space="xxxsmall">
                <Title>
                  {pageType === 'boost-a-listing'
                    ? t(
                        'l.boost_a_listing.flow.title',
                        'Start by creating a listing'
                      )
                    : t(
                        'l.post_a_listing.flow.title',
                        'Create unlimited listings - Always Free'
                      )}
                </Title>
                <Subtitle>
                  {t(
                    'l.post_a_listing.flow.subtitle',
                    "Post your first listing today to take advantage of liv.rent's all-in-one credit check and rental management & marketing tools."
                  )}
                </Subtitle>
              </Stack>
              <Text size="medium">
                <Trans i18nKey="l.post_a_listing.login">
                  Already have an account?{' '}
                  <TextLinkButton onClick={showModal}>
                    Login here
                  </TextLinkButton>
                  .
                </Trans>
              </Text>
            </Stack>
          </Box>
        )}
      </Left>
      <Right>
        <FormWrapper>
          <CardTitle>{titles[currentStep]}</CardTitle>
          <Formik
            // ====== Initial values considers the coupon =====
            initialValues={data}
            onSubmit={onSubmit}
            validationSchema={validationSchemaArray[currentStep]}
            enableReinitialize
          >
            {formikProps => {
              return (
                <StyledForm style={{ width: '100%' }}>
                  <FormComponent
                    formikProps={formikProps}
                    createListing={createListing}
                  />
                  <ActionSection
                    formikProps={formikProps}
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                    clickId={clickId}
                  />
                </StyledForm>
              )
            }}
          </Formik>
        </FormWrapper>
      </Right>
      <DSLoginModal
        mode="login"
        eventId="web-auth-switch-to-login"
        visible={visible}
        onClose={closeModal}
        t={t}
        defaultOperationType={AccountType.LANDLORD}
        autoFocusName={false}
      />
    </Wrapper>
  )
}

export default PostAListing
